import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import FacebookIframe from "./FacebookIframe";
import { getFacebookUrl } from "../helpers/Api/apiHelper";
import { useGsap, gsap } from "../animations/hooks";

const TABS = ["bank", "supplies", "duitnow"];

function DonationWaysTabs() {
    const { t } = useTranslation();
    const staticFbUrl = process.env.REACT_APP_SHANDE_FACEBOOK_SUPPLIES_URL;
    const [active, setActive] = React.useState("bank");
    const [url, setUrl] = React.useState(staticFbUrl);

    React.useEffect(() => {
        let cancelled = false;
        const getMyUrl = async () => {
            try {
                const response = await getFacebookUrl();
                if (!cancelled) setUrl(response?.success?.url ?? staticFbUrl);
            } catch (e) {
                if (!cancelled) setUrl(staticFbUrl);
            }
        };
        getMyUrl();
        return () => { cancelled = true; };
    }, [staticFbUrl]);

    const tabs = [
        { key: "bank", label: t("bankDetails"), icon: "fa-building-columns", hint: t("bankDetailsHint") },
        { key: "supplies", label: t("donationSupplies"), icon: "fa-box-open", hint: t("suppliesHint") },
        { key: "duitnow", label: t("duitNow"), icon: "fa-qrcode", hint: t("duitNowHint") },
    ];
    const current = tabs.find((tab) => tab.key === active);

    // Fade + rise the freshly mounted panel whenever the tab changes.
    const scope = useGsap((gsap, el) => {
        gsap.fromTo(
            el.querySelector(".donation-tab-panel"),
            { y: 24, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.6, ease: "power3.out", clearProps: "transform" }
        );
    }, [active]);

    // Slide the pill indicator under the active tab (and keep it there on resize).
    React.useLayoutEffect(() => {
        const el = scope.current;
        if (!el) return undefined;
        const indicator = el.querySelector(".donation-tab-indicator");
        const move = (animate) => {
            const activeBtn = el.querySelector(`[data-tab="${active}"]`);
            if (!activeBtn || !indicator) return;
            gsap[animate ? "to" : "set"](indicator, {
                x: activeBtn.offsetLeft,
                width: activeBtn.offsetWidth,
                duration: 0.45,
                ease: "power3.inOut",
                overwrite: "auto",
            });
        };
        move(true);
        const onResize = () => move(false);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [active, scope]);

    const onKeyDown = (event) => {
        const index = TABS.indexOf(active);
        if (event.key === "ArrowRight") setActive(TABS[(index + 1) % TABS.length]);
        if (event.key === "ArrowLeft") setActive(TABS[(index - 1 + TABS.length) % TABS.length]);
    };

    return (
        <div ref={scope} className="donation-tabs">
            <div className="donation-tab-list" role="tablist" aria-label={t("donationWays")} onKeyDown={onKeyDown}>
                <span className="donation-tab-indicator" aria-hidden="true" />
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        id={`donation-tab-${tab.key}`}
                        data-tab={tab.key}
                        aria-selected={active === tab.key}
                        aria-controls={`donation-panel-${tab.key}`}
                        tabIndex={active === tab.key ? 0 : -1}
                        className={"donation-tab" + (active === tab.key ? " is-active" : "")}
                        onClick={() => setActive(tab.key)}
                    >
                        <i className={`fa-solid ${tab.icon}`} aria-hidden="true" />
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            <div
                key={active}
                className="donation-tab-panel"
                role="tabpanel"
                id={`donation-panel-${active}`}
                aria-labelledby={`donation-tab-${active}`}
            >
                <p className="donation-tab-hint">{current.hint}</p>
                {active === "bank" && (
                    <>
                        <img className="donation-tab-image" src="assets/img/donation_details/cimb.png" alt={t("bankDetails")} />
                        <Link className="landing-btn landing-btn-ghost donation-tab-cta" to="/donate">{t("viewFullBankDetails")}</Link>
                    </>
                )}
                {active === "supplies" && (
                    <div className="donation-tab-embed">
                        <FacebookIframe url={url} />
                    </div>
                )}
                {active === "duitnow" && (
                    <img className="donation-tab-image donation-tab-qr" src="assets/img/donation_details/duitnow_QR.jpg" alt={t("duitNow")} />
                )}
            </div>
        </div>
    );
}
export default DonationWaysTabs;
